import { forwardRef, ComponentProps, MouseEvent as ReactMouseEvent, useCallback, PropsWithChildren } from 'react';
import NextLink from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn, useNavigationContext } from '@restworld/utils-common';

import { IconComponentType } from '../icon-button';
import { focusRing, focusRingReversed } from '../styles/focus-ring';

const LINK_DELAY = 200;

const linkVariants = cva(
  // Shared WCAG focus ring (purple-500); the reversed variant overrides it to white.
  `flex items-center gap-2 no-underline text-inherit rounded-lg w-fit font-medium transition duration-200 ease-in-out hover:cursor-pointer ${focusRing}`,
  {
    variants: {
      size: {
        sm: 'p-1 text-sm h-8',
        md: 'py-1 px-2 text-base h-10',
      },
      variant: {
        neutral: 'text-neutral-700 hover:bg-neutral-900/10 active:bg-neutral-900/20',
        success: 'text-green-700 hover:bg-green-700/10 active:bg-green-700/20',
        warning: 'text-orange-700 hover:bg-orange-700/10 active:bg-orange-700/20',
        error: 'text-red-700 hover:bg-red-700/10 active:bg-red-700/20',
        restworld: 'text-purple-700 hover:bg-purple-700/10 active:bg-purple-700/20',
        reversed: `text-white hover:bg-white/10 ${focusRingReversed}`,
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'neutral',
    },
  }
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    disabled,
    onClick,
    children,
    className: classNameProp,
    StartIcon,
    EndIcon,
    newTab = true,
    href,
    size = 'md',
    variant = 'neutral',
    ...restProps
  } = props;

  // Action-only mode ("Link or action" in the DS): without an href the
  // component just fires onClick and never navigates.
  const isActionOnly = href === undefined;

  const { router } = useNavigationContext();
  const iconClasses = 'h-4 w-4';

  const handleClick = useCallback(
    (e: ReactMouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      if (disabled) return;

      onClick && onClick(e);

      if (isActionOnly) return;

      setTimeout(() => {
        if (newTab) {
          window.open(href, '_blank', 'noopener noreferrer');
        } else {
          router.push(href);
        }
      }, LINK_DELAY);
    },
    [disabled, href, isActionOnly, newTab, onClick, router]
  );

  const linkClassName = cn(
    linkVariants({ size, variant }),
    disabled ? 'text-neutral-100 hover:bg-transparent hover:cursor-not-allowed' : '',
    classNameProp
  );

  return (
    // Modern next/link renders its own <a> — no more `legacyBehavior`/`passHref` + nested anchor.
    // Navigation is still driven by handleClick (preventDefault + manual router.push/window.open).
    <NextLink
      href={href ?? '#!'}
      ref={ref}
      className={linkClassName}
      onClick={(e) => handleClick(e)}
      target={!isActionOnly && newTab ? '_blank' : undefined}
      rel={!isActionOnly && newTab ? 'noopener noreferrer' : undefined}
      {...restProps}
    >
      {StartIcon && <StartIcon className={iconClasses} />}
      {children}
      {EndIcon && <EndIcon className={iconClasses} />}
    </NextLink>
  );
});
Link.displayName = 'Link';

export default Link;

export interface LinkProps extends ComponentProps<'a'>, PropsWithChildren<StyledLinkProps> {
  StartIcon?: IconComponentType;
  EndIcon?: IconComponentType;
  disabled?: boolean;
  className?: string;
  newTab?: boolean;
}
type LinkSizeType = 'sm' | 'md';
type LinkVariantType = 'neutral' | 'success' | 'warning' | 'error' | 'restworld' | 'reversed';
interface StyledLinkProps {
  size?: LinkSizeType;
  variant?: LinkVariantType;
  disabled?: boolean;
  className?: string;
}

export { linkVariants };
